import {
  NEW_SYNC_EVENT_CONTRACT_VERSION,
  type AgentType,
} from "@cord/shared"
import type { ManagedRuntimeState } from "@/lib/thread-sync-replica-runtime"
import type { ThreadSyncReplicaSourceKind } from "@/lib/thread-sync-replica-provenance"

/**
 * Version two adds a compatible-checkpoint lookup index so restore can jump to
 * the newest matching projector/contract checkpoint instead of cursor-scanning
 * older incompatible rows. Future schema changes should bump this deliberately
 * rather than silently mutating rows.
 */
export const THREAD_REPLICA_IDB_VERSION = 2

/**
 * Browser durable replicas store the same event contract emitted by the new-sync
 * transport. Keep this coupled to the shared wire version so imported R2
 * checkpoints can append subsequent websocket/catchup rows without a false
 * `event_contract_changed` invalidation.
 */
export const THREAD_REPLICA_EVENT_CONTRACT_VERSION = NEW_SYNC_EVENT_CONTRACT_VERSION

/**
 * Checkpoints serialize the current projector state shape. Bump this when the
 * meaning of stored runtime snapshots changes in a way existing replicas
 * cannot self-heal from.
 *
 * - v1: initial checkpoint semantics.
 * - v2: fixes Bug 2 (stale-cachedState checkpoints). Before this fix,
 *   `maybeSaveDurableReplicaCheckpoint` could write a checkpoint stamped at
 *   `entry.resume.cursor` while `cachedState.rawOpenCodeProjection` only
 *   reflected a strict prefix of those events, because
 *   `normalizeManagedRuntimeState` silently rewrote `lastEventId` to match the
 *   cursor. The save path now skips stale pairs and the store rejects them
 *   defensively, but previously-written v1 checkpoints can still be poisoned.
 *   Bumping to v2 invalidates them globally so the next hydrate rebuilds from
 *   the retained runtime lane (or falls back to a cold transport replay for
 *   tail-window-compacted threads).
 *
 * See `agent-docs/browser-cache-terminal-state-signaling/bug-2-stale-checkpoint-from-cached-state/`.
 *
 * - v3: Claude raw projections gained an in-memory derived timeline/index state
 *   and parent-scoped tool ids. Existing persisted snapshots were built with the
 *   old projector shape, so invalidate them instead of mixing old snapshots with
 *   the new incremental projection semantics.
 */
export const THREAD_REPLICA_PROJECTOR_VERSION = 3

/**
 * New replicas start at epoch one so an unset/empty epoch can stay reserved for
 * malformed or missing rows during parsing and recovery.
 */
export const INITIAL_THREAD_REPLICA_EPOCH = 1

/**
 * Cursor zero represents an empty durable prefix because SessionDO cursor
 * semantics are exclusive-after and the first real runtime event starts at one.
 */
export const EMPTY_THREAD_REPLICA_CURSOR = 0

/**
 * Baseline replay sequences are one-based in the transport contract, so a fresh
 * baseline lane expects its first persisted row at sequence one.
 */
export const FIRST_THREAD_REPLICA_SEQUENCE = 1

export const THREAD_REPLICA_IDB_NAME = "cord-thread-sync-replicas"
export const THREAD_REPLICA_META_STORE = "thread_replica_meta"
export const THREAD_REPLICA_EVENTS_STORE = "thread_replica_events"
export const THREAD_REPLICA_CHECKPOINTS_STORE = "thread_replica_checkpoints"

/**
 * The manager does not currently thread repo-scoped identity into the sync
 * layer, while session ids are already globally unique in practice. Use one
 * stable default scope key for the transitional cutover and replace it once the
 * foreground path carries an explicit replica scope.
 */
export const THREAD_REPLICA_DEFAULT_SCOPE_KEY = "global"

export const THREAD_REPLICA_EVENTS_REPLAY_IDENTITY_INDEX = "bySessionLaneReplayEventId"
export const THREAD_REPLICA_CHECKPOINTS_COMPATIBLE_CURSOR_INDEX = "byCompatibleCursor"

export type ThreadReplicaCoverage = "none" | "full" | "tail_window"
export type ThreadReplicaBaselineCoverage = "none" | "partial" | "complete"
export type ThreadReplicaEventLane = "runtime" | "baseline"

export interface ThreadReplicaScope {
  scopeKey: string
  sessionId: string
}

export interface ThreadReplicaMetaRecord extends ThreadReplicaScope {
  replicaEpoch: number
  eventContractVersion: number
  projectorVersion: number
  historyToken: string | null
  browserHistoryEpoch: string | null
  localCursor: number
  oldestAvailableCursor: number
  coverage: ThreadReplicaCoverage
  baselineCoverage: ThreadReplicaBaselineCoverage
  lastBaselineSequence: number
  lastCheckpointCursor: number
  lastUpdatedAtMs: number
  lastValidatedAtMs: number | null
  lastResetReason: string | null
}

export interface ThreadReplicaRuntimeEventInput {
  contractVersion: number
  replayEventId: string
  id: number
  type: string
  timestamp: number
  payload: unknown
  childSessionId?: string | null
  /**
   * Cord runtime-conversation (chat tab) that this raw event belongs to.
   * Events are tagged at the transport layer so multi-conversation sessions can
   * persist a single contiguous event lane in IndexedDB while still letting the
   * browser rebuild a per-conversation projection at hydrate/replay time.
   */
  conversationId?: string | null
}

export interface ThreadReplicaBaselineEventInput {
  contractVersion: number
  replayEventId: string
  baselineSeq: number
  type: string
  timestamp: number
  payload: unknown
  originSessionId?: string | null
  originEventId?: number | null
}

interface ThreadReplicaBaseEventRow extends ThreadReplicaScope {
  replicaEpoch: number
  contractVersion: number
  replayEventId: string
  eventType: string
  payload: unknown
  timestamp: number
  sourceKind: ThreadSyncReplicaSourceKind | null
}

export interface ThreadReplicaRuntimeEventRow extends ThreadReplicaBaseEventRow {
  lane: "runtime"
  sequence: number
  childSessionId: string | null
  /**
   * Cord runtime-conversation this raw row belongs to. `null` means the row was
   * persisted before multi-conversation support; readers MUST normalize missing
   * values to the default conversation id so legacy rows render in the main
   * transcript.
   */
  conversationId: string | null
}

export interface ThreadReplicaBaselineEventRow extends ThreadReplicaBaseEventRow {
  lane: "baseline"
  sequence: number
  originSessionId: string | null
  originEventId: number | null
}

export type ThreadReplicaEventRow =
  | ThreadReplicaRuntimeEventRow
  | ThreadReplicaBaselineEventRow

export interface ThreadReplicaCheckpointRecord extends ThreadReplicaScope {
  replicaEpoch: number
  cursor: number
  lastBaselineSequence: number
  eventContractVersion: number
  projectorVersion: number
  coverage: ThreadReplicaCoverage
  baselineCoverage: ThreadReplicaBaselineCoverage
  agentType: AgentType | null
  runtimeState: ManagedRuntimeState
  createdAtMs: number
  /**
   * Cord runtime-conversation whose projection is captured in this checkpoint.
   * Checkpoints are projection snapshots, not raw-event snapshots, so they can
   * only ever be reused when restoring the same conversation's transcript. A
   * null value means the checkpoint was written before multi-conversation
   * support and is treated as a default-conversation checkpoint. Hydration for
   * any other conversation MUST reject a checkpoint whose conversation id does
   * not match the active one.
   */
  conversationId: string | null
}

export interface ThreadReplicaServerResumeContract {
  localCursor: number
  historyToken: string | null
}

export type ThreadReplicaAppendResult =
  | {
      status: "appended" | "duplicate"
      meta: ThreadReplicaMetaRecord
      appendedCount: number
      duplicateCount: number
    }
  | {
      status: "stale_writer"
      currentEpoch: number
    }
  | {
      status: "gap"
      currentSequence: number
      expectedAfter: number
      reason:
        | "before_oldest_available"
        | "after_current_tail"
        | "non_contiguous_input"
    }
  | {
      status: "conflict"
      nextEpoch: number
      reason:
        | "event_contract_changed"
        | "replay_identity_conflict"
        | "conflicting_overlap"
    }
  | {
      status: "storage_unavailable"
    }

export type ThreadReplicaHydrateResult =
  | {
      status: "hydrated"
      meta: ThreadReplicaMetaRecord
      runtimeState: ManagedRuntimeState
      targetCursor: number
      checkpointCursor: number | null
      baselineReplayCount: number
      runtimeReplayCount: number
    }
  | {
      status: "missing"
      reason: "storage_unavailable" | "missing_meta" | "empty_replica"
    }
  | {
      status: "reset_required"
      reason: "integrity_gap"
      meta: ThreadReplicaMetaRecord
      targetCursor: number
    }

function normalizeNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function normalizeNonNegativeInteger(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < EMPTY_THREAD_REPLICA_CURSOR) {
    return null
  }

  return Math.floor(value)
}

export function normalizePositiveInteger(value: unknown): number | null {
  const normalized = normalizeNonNegativeInteger(value)
  if (normalized === null || normalized < FIRST_THREAD_REPLICA_SEQUENCE) {
    return null
  }

  return normalized
}

export function normalizeThreadReplicaCoverage(value: unknown): ThreadReplicaCoverage | null {
  if (value === "none" || value === "full" || value === "tail_window") {
    return value
  }

  return null
}

export function normalizeThreadReplicaBaselineCoverage(
  value: unknown,
): ThreadReplicaBaselineCoverage | null {
  if (value === "none" || value === "partial" || value === "complete") {
    return value
  }

  return null
}

export function buildEmptyThreadReplicaMeta(args: {
  scopeKey: string
  sessionId: string
  nowMs: number
}): ThreadReplicaMetaRecord {
  return {
    scopeKey: args.scopeKey,
    sessionId: args.sessionId,
    replicaEpoch: INITIAL_THREAD_REPLICA_EPOCH,
    eventContractVersion: THREAD_REPLICA_EVENT_CONTRACT_VERSION,
    projectorVersion: THREAD_REPLICA_PROJECTOR_VERSION,
    historyToken: null,
    browserHistoryEpoch: null,
    localCursor: EMPTY_THREAD_REPLICA_CURSOR,
    oldestAvailableCursor: EMPTY_THREAD_REPLICA_CURSOR,
    coverage: "none",
    baselineCoverage: "none",
    lastBaselineSequence: EMPTY_THREAD_REPLICA_CURSOR,
    lastCheckpointCursor: EMPTY_THREAD_REPLICA_CURSOR,
    lastUpdatedAtMs: Math.max(EMPTY_THREAD_REPLICA_CURSOR, Math.floor(args.nowMs)),
    lastValidatedAtMs: null,
    lastResetReason: null,
  }
}

export function normalizeThreadReplicaMetaRecord(value: unknown): ThreadReplicaMetaRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, unknown>
  const scopeKey = normalizeNonEmptyString(raw.scopeKey)
  const sessionId = normalizeNonEmptyString(raw.sessionId)
  const replicaEpoch = normalizePositiveInteger(raw.replicaEpoch)
  const eventContractVersion = normalizePositiveInteger(raw.eventContractVersion)
  const projectorVersion = normalizePositiveInteger(raw.projectorVersion)
  const localCursor = normalizeNonNegativeInteger(raw.localCursor)
  const oldestAvailableCursor = normalizeNonNegativeInteger(raw.oldestAvailableCursor)
  const lastBaselineSequence = normalizeNonNegativeInteger(raw.lastBaselineSequence)
  const lastCheckpointCursor = normalizeNonNegativeInteger(raw.lastCheckpointCursor)
  const lastUpdatedAtMs = normalizeNonNegativeInteger(raw.lastUpdatedAtMs)
  const lastValidatedAtMs =
    raw.lastValidatedAtMs === null ? null : normalizeNonNegativeInteger(raw.lastValidatedAtMs)
  const historyToken = raw.historyToken === null ? null : normalizeNonEmptyString(raw.historyToken)
  const browserHistoryEpoch =
    raw.browserHistoryEpoch === null ? null : normalizeNonEmptyString(raw.browserHistoryEpoch)
  const coverage = normalizeThreadReplicaCoverage(raw.coverage)
  const baselineCoverage = normalizeThreadReplicaBaselineCoverage(raw.baselineCoverage)
  const lastResetReason =
    raw.lastResetReason === null ? null : normalizeNonEmptyString(raw.lastResetReason)

  if (
    !scopeKey ||
    !sessionId ||
    replicaEpoch === null ||
    eventContractVersion === null ||
    projectorVersion === null ||
    localCursor === null ||
    oldestAvailableCursor === null ||
    lastBaselineSequence === null ||
    lastCheckpointCursor === null ||
    lastUpdatedAtMs === null ||
    (raw.lastValidatedAtMs !== null && lastValidatedAtMs === null) ||
    (raw.historyToken !== null && historyToken === null) ||
    (raw.browserHistoryEpoch !== null && browserHistoryEpoch === null) ||
    coverage === null ||
    baselineCoverage === null ||
    (raw.lastResetReason !== null && lastResetReason === null)
  ) {
    return null
  }

  return {
    scopeKey,
    sessionId,
    replicaEpoch,
    eventContractVersion,
    projectorVersion,
    historyToken,
    browserHistoryEpoch,
    localCursor,
    oldestAvailableCursor,
    coverage,
    baselineCoverage,
    lastBaselineSequence,
    lastCheckpointCursor,
    lastUpdatedAtMs,
    lastValidatedAtMs,
    lastResetReason,
  }
}

export function normalizeThreadReplicaEventLane(value: unknown): ThreadReplicaEventLane | null {
  if (value === "runtime" || value === "baseline") {
    return value
  }

  return null
}

export function normalizeThreadReplicaRuntimeEventRow(
  value: unknown,
): ThreadReplicaRuntimeEventRow | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, unknown>
  if (normalizeThreadReplicaEventLane(raw.lane) !== "runtime") {
    return null
  }

  const scopeKey = normalizeNonEmptyString(raw.scopeKey)
  const sessionId = normalizeNonEmptyString(raw.sessionId)
  const replicaEpoch = normalizePositiveInteger(raw.replicaEpoch)
  const contractVersion = normalizePositiveInteger(raw.contractVersion)
  const replayEventId = normalizeNonEmptyString(raw.replayEventId)
  const sequence = normalizePositiveInteger(raw.sequence)
  const eventType = normalizeNonEmptyString(raw.eventType)
  const timestamp = normalizeNonNegativeInteger(raw.timestamp)
  const childSessionId = raw.childSessionId === null ? null : normalizeNonEmptyString(raw.childSessionId)
  const sourceKind = raw.sourceKind === null ? null : (normalizeNonEmptyString(raw.sourceKind) as ThreadSyncReplicaSourceKind | null)
  // Rows written before multi-conversation tagging do not have a conversationId
  // field. Accept them and leave conversationId null; callers normalize null to
  // the default conversation so legacy single-chat data keeps rendering.
  const conversationId =
    raw.conversationId === null || raw.conversationId === undefined
      ? null
      : normalizeNonEmptyString(raw.conversationId)

  if (
    !scopeKey ||
    !sessionId ||
    replicaEpoch === null ||
    contractVersion === null ||
    !replayEventId ||
    sequence === null ||
    !eventType ||
    timestamp === null ||
    (raw.childSessionId !== null && raw.childSessionId !== undefined && childSessionId === null)
  ) {
    return null
  }

  return {
    scopeKey,
    sessionId,
    replicaEpoch,
    contractVersion,
    replayEventId,
    lane: "runtime",
    sequence,
    eventType,
    payload: raw.payload,
    timestamp,
    childSessionId,
    conversationId,
    sourceKind,
  }
}

export function normalizeThreadReplicaBaselineEventRow(
  value: unknown,
): ThreadReplicaBaselineEventRow | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, unknown>
  if (normalizeThreadReplicaEventLane(raw.lane) !== "baseline") {
    return null
  }

  const scopeKey = normalizeNonEmptyString(raw.scopeKey)
  const sessionId = normalizeNonEmptyString(raw.sessionId)
  const replicaEpoch = normalizePositiveInteger(raw.replicaEpoch)
  const contractVersion = normalizePositiveInteger(raw.contractVersion)
  const replayEventId = normalizeNonEmptyString(raw.replayEventId)
  const sequence = normalizePositiveInteger(raw.sequence)
  const eventType = normalizeNonEmptyString(raw.eventType)
  const timestamp = normalizeNonNegativeInteger(raw.timestamp)
  const originSessionId =
    raw.originSessionId === null ? null : normalizeNonEmptyString(raw.originSessionId)
  const originEventId =
    raw.originEventId === null ? null : normalizeNonNegativeInteger(raw.originEventId)
  const sourceKind = raw.sourceKind === null ? null : (normalizeNonEmptyString(raw.sourceKind) as ThreadSyncReplicaSourceKind | null)

  if (
    !scopeKey ||
    !sessionId ||
    replicaEpoch === null ||
    contractVersion === null ||
    !replayEventId ||
    sequence === null ||
    !eventType ||
    timestamp === null ||
    (raw.originSessionId !== null && raw.originSessionId !== undefined && originSessionId === null) ||
    (raw.originEventId !== null && raw.originEventId !== undefined && originEventId === null)
  ) {
    return null
  }

  return {
    scopeKey,
    sessionId,
    replicaEpoch,
    contractVersion,
    replayEventId,
    lane: "baseline",
    sequence,
    eventType,
    payload: raw.payload,
    timestamp,
    originSessionId,
    originEventId,
    sourceKind,
  }
}

export function normalizeThreadReplicaEventRow(value: unknown): ThreadReplicaEventRow | null {
  return (
    normalizeThreadReplicaRuntimeEventRow(value) ??
    normalizeThreadReplicaBaselineEventRow(value)
  )
}

export function normalizeThreadReplicaCheckpointRecord(
  value: unknown,
): ThreadReplicaCheckpointRecord | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null
  }

  const raw = value as Record<string, unknown>
  const scopeKey = normalizeNonEmptyString(raw.scopeKey)
  const sessionId = normalizeNonEmptyString(raw.sessionId)
  const replicaEpoch = normalizePositiveInteger(raw.replicaEpoch)
  const cursor = normalizeNonNegativeInteger(raw.cursor)
  const lastBaselineSequence = normalizeNonNegativeInteger(raw.lastBaselineSequence)
  const eventContractVersion = normalizePositiveInteger(raw.eventContractVersion)
  const projectorVersion = normalizePositiveInteger(raw.projectorVersion)
  const coverage = normalizeThreadReplicaCoverage(raw.coverage)
  const baselineCoverage = normalizeThreadReplicaBaselineCoverage(raw.baselineCoverage)
  const createdAtMs = normalizeNonNegativeInteger(raw.createdAtMs)

  if (
    !scopeKey ||
    !sessionId ||
    replicaEpoch === null ||
    cursor === null ||
    lastBaselineSequence === null ||
    eventContractVersion === null ||
    projectorVersion === null ||
    coverage === null ||
    baselineCoverage === null ||
    createdAtMs === null
  ) {
    return null
  }

  const conversationId =
    raw.conversationId === null || raw.conversationId === undefined
      ? null
      : normalizeNonEmptyString(raw.conversationId)

  return {
    scopeKey,
    sessionId,
    replicaEpoch,
    cursor,
    lastBaselineSequence,
    eventContractVersion,
    projectorVersion,
    coverage,
    baselineCoverage,
    agentType: (raw.agentType as AgentType | null | undefined) ?? null,
    runtimeState: raw.runtimeState as ManagedRuntimeState,
    createdAtMs,
    conversationId,
  }
}
